import React, { useState, useEffect, useCallback } from 'react';
import generateUUID from '../utils/uuidGenerator';
import { DatabaseProvider, useDatabase } from '../context/DatabaseContext';
import ResponsiveLayout from './database/ResponsiveLayout';
import ResizablePanel from './database/ResizablePanel';
import TabView from './database/TabView';
import TableViewer from './database/TableViewer';
import SchemaViewer from './database/SchemaViewer';
import SqlRunner from './database/SqlRunner';
import TableCreator from './database/TableCreator';
import TableListManager from './database/TableListManager';
import AdvancedFilter from './database/AdvancedFilter';
import LoadingIndicator from './common/LoadingIndicator';

// Tab type definitions for different view types
const TAB_TYPES = {
  SQL_RUNNER: 'sqlRunner',
  TABLE_VIEWER: 'tableViewer',
  SCHEMA_VIEWER: 'schemaViewer',
  TABLE_LIST_MANAGER: 'tableListManager',
  TABLE_CREATOR: 'tableCreator'
};

/**
 * Database viewer icons for different tab types
 */
const TAB_ICONS = {
  [TAB_TYPES.SQL_RUNNER]: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  [TAB_TYPES.TABLE_VIEWER]: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  ),
  [TAB_TYPES.SCHEMA_VIEWER]: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  ),
  [TAB_TYPES.TABLE_LIST_MANAGER]: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  [TAB_TYPES.TABLE_CREATOR]: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
};

/**
 * DatabaseViewerContent - Inner component that uses the DatabaseContext
 */
const DatabaseViewerContent = ({ password }) => {
  const {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    tables,
    refreshTables,
    selectTable,
    selectedTable
  } = useDatabase();

  // States for layout and tabs
  const [layoutMode, setLayoutMode] = useState('single'); // 'single', 'horizontal', 'vertical'
  const [splitSize, setSplitSize] = useState(50);
  const [leftTabs, setLeftTabs] = useState([]);
  const [rightTabs, setRightTabs] = useState([]);
  const [activeLeftTab, setActiveLeftTab] = useState(null);
  const [activeRightTab, setActiveRightTab] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDraggingTab, setIsDraggingTab] = useState(false);
  const [dropTargetPanel, setDropTargetPanel] = useState(null);

  // Create a new tab
  const createTab = useCallback((type, target = 'left', props = {}) => {
    const tabId = generateUUID();
    
    let title;
    let component;
    
    switch (type) {
      case TAB_TYPES.SQL_RUNNER:
        title = 'SQL Query';
        component = <SqlRunner password={password} viewId={tabId} />;
        break;
      case TAB_TYPES.TABLE_VIEWER:
        title = props.tableName ? `Table: ${props.tableName}` : 'Table Viewer';
        component = <TableViewer tableName={props.tableName} viewId={tabId} password={password} />;
        break;
      case TAB_TYPES.SCHEMA_VIEWER:
        title = props.tableName ? `Schema: ${props.tableName}` : 'Schema Viewer';
        component = <SchemaViewer tableName={props.tableName} viewId={tabId} password={password} />;
        break;
      case TAB_TYPES.TABLE_LIST_MANAGER:
        title = 'Manage Tables';
        component = <TableListManager password={password} onRefreshTables={refreshTables} />;
        break;
      case TAB_TYPES.TABLE_CREATOR:
        title = 'Create Table';
        component = (
          <TableCreator 
            onTableCreated={(tableName) => {
              refreshTables();
              selectTable(tableName);
            }}
            existingTables={tables}
            onRefreshTables={refreshTables}
          />
        );
        break;
      default:
        title = 'New Tab';
        component = <div>Unknown tab type</div>;
    }
    
    const newTab = {
      id: tabId,
      title,
      type,
      icon: TAB_ICONS[type],
      closable: true,
      content: component,
      props
    };
    
    if (target === 'left') {
      setLeftTabs(tabs => [...tabs, newTab]);
      setActiveLeftTab(tabId);
    } else {
      setRightTabs(tabs => [...tabs, newTab]);
      setActiveRightTab(tabId);
    }
    
    return tabId;
  }, [password, refreshTables, selectTable, tables]);

  // Handle tab close
  const handleTabClose = useCallback((tabId, side = 'left') => {
    if (side === 'left') {
      // Remove the tab
      setLeftTabs(tabs => tabs.filter(tab => tab.id !== tabId));
      
      // Update active tab if needed
      if (activeLeftTab === tabId) {
        const remainingTabs = leftTabs.filter(tab => tab.id !== tabId);
        if (remainingTabs.length > 0) {
          setActiveLeftTab(remainingTabs[0].id);
        } else {
          setActiveLeftTab(null);
        }
      }
    } else {
      // Remove the tab
      setRightTabs(tabs => tabs.filter(tab => tab.id !== tabId));
      
      // Update active tab if needed
      if (activeRightTab === tabId) {
        const remainingTabs = rightTabs.filter(tab => tab.id !== tabId);
        if (remainingTabs.length > 0) {
          setActiveRightTab(remainingTabs[0].id);
        } else {
          setActiveRightTab(null);
        }
      }
    }
  }, [activeLeftTab, activeRightTab, leftTabs, rightTabs]);

  // Add default tabs on component mount
  useEffect(() => {
    if (isConnected && !isInitialized) {
      // Create default tabs
      const sqlTabId = createTab(TAB_TYPES.SQL_RUNNER, 'left');

      // Create a TableViewer tab with a default table if tables are available
      if (tables.length > 0) {
        // Look for "configurations" table as the preferred default
        const defaultTableName = tables.includes("configurations") ? "configurations" : tables[0];

        // Select the default table
        selectTable(defaultTableName);

        // Create TableViewer with the default table
        const tableViewerId = createTab(TAB_TYPES.TABLE_VIEWER, 'right', { tableName: defaultTableName });
        setActiveRightTab(tableViewerId);
      } else {
        // If no tables are available, create an empty table viewer
        const tableViewerId = createTab(TAB_TYPES.TABLE_VIEWER, 'right');
        setActiveRightTab(tableViewerId);
      }

      // Set active left tab
      setActiveLeftTab(sqlTabId);

      setIsInitialized(true);
    }
  }, [isConnected, isInitialized, createTab, tables, selectTable]);

  // Connect to the database when the component mounts
  useEffect(() => {
    if (password && !isConnected && !isConnecting) {
      connect(password).then(() => {
        // After successful connection, refresh the tables list
        refreshTables();
      });
    } else if (password && isConnected && tables.length === 0) {
      // If already connected but no tables loaded, refresh tables
      refreshTables();
    }
  }, [password, isConnected, isConnecting, connect, refreshTables, tables.length]);

  // When tables are loaded, automatically select and open "configurations" if it exists
  useEffect(() => {
    if (isConnected && tables.length > 0) {
      // Look for "configurations" table as the preferred default
      const configTableExists = tables.includes("configurations");
      if (configTableExists) {
        // Select the configurations table
        selectTable("configurations");

        // Create a table viewer tab for configurations if needed
        const configTabExists = [...leftTabs, ...rightTabs].some(
          tab => tab.type === TAB_TYPES.TABLE_VIEWER && tab.props && tab.props.tableName === "configurations"
        );

        if (!configTabExists) {
          // Open configurations table in a new tab
          openTableInTab("configurations", TAB_TYPES.TABLE_VIEWER, "right");
        }
      }
    }
  }, [isConnected, tables]);

  // Handle tab dragging between panels
  const handleTabCrossPanel = useCallback((sourcePanel, targetPanel, tabData) => {
    // Only process if we have two panels open
    if (layoutMode === 'single') return;

    // Get the tab ID and type data
    const tabId = tabData.id;
    const tabType = tabData.type;
    const tabProps = tabData.props || {};

    // Check if the tab is in the source panel
    if (sourcePanel === 'left' && leftTabs.find(tab => tab.id === tabId)) {
      // Remove from left panel
      const updatedLeftTabs = leftTabs.filter(tab => tab.id !== tabId);
      setLeftTabs(updatedLeftTabs);

      // Create a new tab in the right panel with the same properties
      const newTabId = createTab(tabType, 'right', tabProps);
      setActiveRightTab(newTabId);

      // Update active left tab if needed
      if (activeLeftTab === tabId) {
        if (updatedLeftTabs.length > 0) {
          setActiveLeftTab(updatedLeftTabs[0].id);
        } else {
          setActiveLeftTab(null);
        }
      }
    } else if (sourcePanel === 'right' && rightTabs.find(tab => tab.id === tabId)) {
      // Remove from right panel
      const updatedRightTabs = rightTabs.filter(tab => tab.id !== tabId);
      setRightTabs(updatedRightTabs);

      // Create a new tab in the left panel with the same properties
      const newTabId = createTab(tabType, 'left', tabProps);
      setActiveLeftTab(newTabId);

      // Update active right tab if needed
      if (activeRightTab === tabId) {
        if (updatedRightTabs.length > 0) {
          setActiveRightTab(updatedRightTabs[0].id);
        } else {
          setActiveRightTab(null);
        }
      }
    }
  }, [layoutMode, leftTabs, rightTabs, activeLeftTab, activeRightTab, createTab]);

  // Event listener for cross-panel tab drops and drag events
  // Memoized function to handle tab splitting
  const handleTabSplitOperations = useCallback((tabId, direction) => {
    // Find the tab data regardless of which panel it's in
    const leftTabIndex = leftTabs.findIndex(tab => tab.id === tabId);
    const rightTabIndex = rightTabs.findIndex(tab => tab.id === tabId);

    let tabType = null;
    let tabProps = {};
    let tabPanel = '';

    if (leftTabIndex !== -1) {
      const tabData = leftTabs[leftTabIndex];
      tabType = tabData.type;
      tabProps = tabData.props || {};
      tabPanel = 'left';
    } else if (rightTabIndex !== -1) {
      const tabData = rightTabs[rightTabIndex];
      tabType = tabData.type;
      tabProps = tabData.props || {};
      tabPanel = 'right';
    }

    if (!tabType) return;

    // Set the appropriate layout mode based on the direction
    if (direction === 'horizontal') {
      setLayoutMode('horizontal');
    } else if (direction === 'vertical') {
      setLayoutMode('vertical');
    }

    // Create a new tab in the target panel
    if (tabPanel === 'left') {
      // Create a new tab in the right panel with the same type and props
      const newTabId = createTab(tabType, 'right', tabProps);
      setActiveRightTab(newTabId);
    } else if (tabPanel === 'right') {
      // Create a new tab in the left panel with the same type and props
      const newTabId = createTab(tabType, 'left', tabProps);
      setActiveLeftTab(newTabId);
    }
  }, [leftTabs, rightTabs, createTab]);

  useEffect(() => {
    const handleTabCrossPanelDrop = (e) => {
      const { tabData, sourcePanel, targetPanel } = e.detail;
      handleTabCrossPanel(sourcePanel, targetPanel, tabData);
      setIsDraggingTab(false);
      setDropTargetPanel(null);
    };

    const handleTabDragStart = (e) => {
      setIsDraggingTab(true);
    };

    const handleTabDragEnd = (e) => {
      setIsDraggingTab(false);
      setDropTargetPanel(null);
    };

    // Handle tab split events - now using the memoized function
    const handleTabSplitEvent = (e) => {
      const { tabId, direction } = e.detail;
      handleTabSplitOperations(tabId, direction);
    };

    // Handle panel drag enter events to highlight potential drop targets
    const handlePanelDragEnter = (e) => {
      if (!isDraggingTab) return;

      const panelElement = e.currentTarget;
      if (panelElement.classList.contains('left-panel')) {
        setDropTargetPanel('left');
      } else if (panelElement.classList.contains('right-panel')) {
        setDropTargetPanel('right');
      }
    };

    document.addEventListener('tab-cross-panel-drop', handleTabCrossPanelDrop);
    document.addEventListener('tab-drag-start', handleTabDragStart);
    document.addEventListener('tab-drag-end', handleTabDragEnd);
    document.addEventListener('tab-split', handleTabSplitEvent);

    // Add drag enter listeners to panel elements
    const leftPanelElements = document.querySelectorAll('.left-panel');
    const rightPanelElements = document.querySelectorAll('.right-panel');

    leftPanelElements.forEach(el => {
      el.addEventListener('dragenter', handlePanelDragEnter);
    });

    rightPanelElements.forEach(el => {
      el.addEventListener('dragenter', handlePanelDragEnter);
    });

    return () => {
      document.removeEventListener('tab-cross-panel-drop', handleTabCrossPanelDrop);
      document.removeEventListener('tab-drag-start', handleTabDragStart);
      document.removeEventListener('tab-drag-end', handleTabDragEnd);
      document.removeEventListener('tab-split', handleTabSplitEvent);

      leftPanelElements.forEach(el => {
        el.removeEventListener('dragenter', handlePanelDragEnter);
      });

      rightPanelElements.forEach(el => {
        el.removeEventListener('dragenter', handlePanelDragEnter);
      });
    };
  }, [handleTabCrossPanel, handleTabSplitOperations, isDraggingTab]);

  // Create a panel with drag-and-drop functionality
  const createDraggablePanel = (side, children) => {
    const isLeft = side === 'left';
    const targetPanel = isLeft ? 'left' : 'right';

    return (
      <div
        className={`h-full ${side}-panel relative ${
          isDraggingTab && dropTargetPanel === targetPanel
            ? 'ring-2 ring-blue-400 ring-inset'
            : ''
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          setDropTargetPanel(targetPanel);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setDropTargetPanel(targetPanel);
        }}
        onDrop={(e) => {
          e.preventDefault();
          try {
            const tabDataStr = e.dataTransfer.getData('application/json');
            const panelSource = e.dataTransfer.getData('panel-source');

            if (tabDataStr && panelSource && panelSource !== targetPanel) {
              const tabData = JSON.parse(tabDataStr);
              handleTabCrossPanel(panelSource, targetPanel, tabData);
            }
          } catch (error) {
            console.error('Error handling panel drop:', error);
          }
          setIsDraggingTab(false);
          setDropTargetPanel(null);
        }}
      >
        {isDraggingTab && dropTargetPanel === targetPanel && (
          <div className="absolute inset-0 bg-blue-100 bg-opacity-20 pointer-events-none z-10 flex items-center justify-center">
            <div className="bg-blue-100 rounded-lg p-2 shadow-lg">
              <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
            </div>
          </div>
        )}
        {children}
      </div>
    );
  };

  // Change layout mode
  const toggleLayout = () => {
    if (layoutMode === 'single') {
      setLayoutMode('horizontal');

      // Create a default tab on the right side if none exists
      if (rightTabs.length === 0) {
        const tableViewerId = createTab(TAB_TYPES.TABLE_VIEWER, 'right');
        setActiveRightTab(tableViewerId);
      }
    } else if (layoutMode === 'horizontal') {
      setLayoutMode('vertical');
    } else {
      setLayoutMode('single');
    }
  };

  // Open a table in a new tab
  const openTableInTab = (tableName, tabType = TAB_TYPES.TABLE_VIEWER, target = 'active') => {
    selectTable(tableName);
    
    // Determine which side is active
    let side = 'left';
    if (target === 'active') {
      if (layoutMode === 'single') {
        side = 'left';
      } else if (document.activeElement) {
        // Try to determine which side has focus
        const activeElement = document.activeElement;
        const leftPanel = document.querySelector('.left-panel');
        if (leftPanel && leftPanel.contains(activeElement)) {
          side = 'left';
        } else {
          side = 'right';
        }
      }
    } else {
      side = target;
    }
    
    // Create the tab
    const props = { tableName };
    const tabId = createTab(tabType, side, props);
    
    // Set as active tab
    if (side === 'left') {
      setActiveLeftTab(tabId);
    } else {
      setActiveRightTab(tabId);
    }
    
    return tabId;
  };

  // Render different components based on connection state
  if (isConnecting) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <LoadingIndicator size="large" text="Connecting to database..." />
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="p-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p className="font-bold">Connection Error</p>
          <p>{connectionError}</p>
        </div>
        <button
          onClick={() => connect(password)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="flex justify-center items-center h-full p-8">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Database Not Connected</h3>
          <p className="text-gray-500 mb-4">Please log in to access the database.</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout minHeight="100vh" className="shadow-lg">
      <div className="h-full flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center border-b border-gray-200 bg-gray-100 p-2 shadow-sm">
          {/* Database actions */}
          <div className="flex space-x-1">
            <button
              onClick={() => createTab(TAB_TYPES.SQL_RUNNER, 'left')}
              className="px-2 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              title="New SQL Query"
            >
              {TAB_ICONS[TAB_TYPES.SQL_RUNNER]} <span className="ml-1">SQL</span>
            </button>
            
            <button
              onClick={() => createTab(TAB_TYPES.TABLE_LIST_MANAGER, 'left')}
              className="px-2 py-1 text-sm bg-green-50 text-green-600 rounded hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
              title="Manage Tables"
            >
              {TAB_ICONS[TAB_TYPES.TABLE_LIST_MANAGER]} <span className="ml-1">Manage Tables</span>
            </button>
          </div>
          
          {/* Tables dropdown */}
          <div className="flex items-center ml-4">
            <label htmlFor="tableSelect" className="sr-only">Select Table</label>
            <select
              id="tableSelect"
              value={selectedTable || ''}
              onChange={(e) => {
                const table = e.target.value;
                if (table) {
                  openTableInTab(table);
                }
              }}
              className="block pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
            >
              <option value="">Select a table</option>
              {tables.map((table) => (
                <option key={table} value={table}>
                  {table}
                </option>
              ))}
            </select>
            
            <button
              onClick={refreshTables}
              className="ml-1 p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
              title="Refresh Tables"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          
          {/* Layout controls - moved to center with spacer div to avoid conflict with fullscreen emblem */}
          <div className="flex-grow flex justify-center space-x-1">
            <div className="flex border rounded bg-gray-50">
              <button
                onClick={() => setLayoutMode('single')}
                className={`p-1.5 ${layoutMode === 'single' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                title="Single view"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </button>

              <button
                onClick={() => {
                  setLayoutMode('horizontal');
                  // Create a default tab on the right side if none exists
                  if (rightTabs.length === 0) {
                    // Try to create a tab with the same table as the active left tab
                    let tableName = selectedTable || null;
                    if (activeLeftTab && !tableName) {
                      const activeTab = leftTabs.find(tab => tab.id === activeLeftTab);
                      if (activeTab && activeTab.props && activeTab.props.tableName) {
                        tableName = activeTab.props.tableName;
                      }
                    }
                    const tableViewerId = createTab(TAB_TYPES.TABLE_VIEWER, 'right', { tableName });
                    setActiveRightTab(tableViewerId);
                  }
                }}
                className={`p-1.5 ${layoutMode === 'horizontal' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                title="Horizontal split (side by side)"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M4 12h16M4 19h16" />
                </svg>
              </button>

              <button
                onClick={() => {
                  setLayoutMode('vertical');
                  // Create a default tab on the right side if none exists
                  if (rightTabs.length === 0) {
                    // Try to create a tab with the same table as the active left tab
                    let tableName = selectedTable || null;
                    if (activeLeftTab && !tableName) {
                      const activeTab = leftTabs.find(tab => tab.id === activeLeftTab);
                      if (activeTab && activeTab.props && activeTab.props.tableName) {
                        tableName = activeTab.props.tableName;
                      }
                    }
                    const tableViewerId = createTab(TAB_TYPES.TABLE_VIEWER, 'right', { tableName });
                    setActiveRightTab(tableViewerId);
                  }
                }}
                className={`p-1.5 ${layoutMode === 'vertical' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                title="Vertical split (top and bottom)"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v18m9-18v18" />
                </svg>
              </button>
            </div>

            {/* Spacer div to ensure layout controls don't overlap with fullscreen emblem */}
            <div className="flex-grow"></div>
          </div>
        </div>
        
        {/* Main content area */}
        <div className="flex-grow overflow-hidden">
          {layoutMode === 'single' && createDraggablePanel('left',
            <TabView
              tabs={leftTabs}
              activeTabId={activeLeftTab}
              onTabChange={setActiveLeftTab}
              onTabClose={(tabId) => handleTabClose(tabId, 'left')}
              onTabAdd={() => createTab(TAB_TYPES.SQL_RUNNER, 'left')}
              fillHeight
            />
          )}
          
          {layoutMode === 'horizontal' && (
            <ResizablePanel
              direction="horizontal"
              initialSize={splitSize}
              minSize={20}
              onResize={setSplitSize}
              className="h-full"
            >
              <div>
                {createDraggablePanel('left',
                  <TabView
                    tabs={leftTabs}
                    activeTabId={activeLeftTab}
                    onTabChange={setActiveLeftTab}
                    onTabClose={(tabId) => handleTabClose(tabId, 'left')}
                    onTabAdd={() => createTab(TAB_TYPES.SQL_RUNNER, 'left')}
                    fillHeight
                  />
                )}
              </div>
              <div>
                {createDraggablePanel('right',
                  <TabView
                    tabs={rightTabs}
                    activeTabId={activeRightTab}
                    onTabChange={setActiveRightTab}
                    onTabClose={(tabId) => handleTabClose(tabId, 'right')}
                    onTabAdd={() => createTab(TAB_TYPES.TABLE_VIEWER, 'right')}
                    fillHeight
                  />
                )}
              </div>
            </ResizablePanel>
          )}
          
          {layoutMode === 'vertical' && (
            <ResizablePanel
              direction="vertical"
              initialSize={splitSize}
              minSize={20}
              onResize={setSplitSize}
              className="h-full"
            >
              <div>
                {createDraggablePanel('left',
                  <TabView
                    tabs={leftTabs}
                    activeTabId={activeLeftTab}
                    onTabChange={setActiveLeftTab}
                    onTabClose={(tabId) => handleTabClose(tabId, 'left')}
                    onTabAdd={() => createTab(TAB_TYPES.SQL_RUNNER, 'left')}
                    fillHeight
                  />
                )}
              </div>
              <div>
                {createDraggablePanel('right',
                  <TabView
                    tabs={rightTabs}
                    activeTabId={activeRightTab}
                    onTabChange={setActiveRightTab}
                    onTabClose={(tabId) => handleTabClose(tabId, 'right')}
                    onTabAdd={() => createTab(TAB_TYPES.TABLE_VIEWER, 'right')}
                    fillHeight
                  />
                )}
              </div>
            </ResizablePanel>
          )}
        </div>
        
        {/* Status bar */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-1 text-xs text-gray-500">
          <div>
            <span className="font-medium text-gray-700">{tables.length}</span> tables
            {selectedTable && <span> | Selected: <span className="font-medium text-gray-700">{selectedTable}</span></span>}
          </div>
          <div>
            {layoutMode !== 'single' && <span>Split: {Math.round(splitSize)}% / {Math.round(100 - splitSize)}%</span>}
          </div>
        </div>
      </div>
    </ResponsiveLayout>
  );
};

/**
 * DatabaseViewer - Main component that wraps the content in the DatabaseProvider
 */
const DatabaseViewer = ({ password }) => {
  return (
    <DatabaseProvider password={password}>
      <DatabaseViewerContent password={password} />
    </DatabaseProvider>
  );
};

export default DatabaseViewer;